import { atom } from 'jotai'
import type { createStore } from 'jotai'
import { hc } from 'hono/client'
import type { AuthApi } from '@backend/auth'
import { apiFetch, FormError, AuthRequiredError } from '@/lib/apiFetch'
import { setUser, clearUser } from '@/lib/db'
import { ensureBootstrapped, pushAllPending, setLoggedIn } from '@/lib/sync'

type Store = ReturnType<typeof createStore>
const api = hc<AuthApi>('/api/auth', { fetch: apiFetch })

type SessionUser = { id: string; phone: string; role: string } | null

export const phoneAtom = atom('')
export const passwordAtom = atom('')
export const isSubmittingAtom = atom(false)

// Register mode
export const modeAtom = atom<'login' | 'register'>('login')
export const confirmPasswordAtom = atom('')
export const inviteCodeAtom = atom('')

async function hydrateLocalForUser(user: { id: string; phone: string; role: string }) {
  // Local-first: never wipe local data on login. Guest data is merged with
  // server data via the existing pull/push pipeline. Single-device login is
  // enforced server-side, so we don't expect another account's data to be
  // sitting in this IndexedDB.
  setLoggedIn(true)
  await setUser({ id: user.id, phone: user.phone, role: user.role as 'user' | 'admin' })
  // First-time pull populates local DB. Non-fatal — next app load will retry.
  try {
    await ensureBootstrapped()
  } catch (e) {
    console.warn('bootstrap pull failed, will retry on next app load', e)
  }
  // Push any pending local writes (including guest-mode writes) up to the server
  void pushAllPending().catch(() => {})
}

export const submitAtom = atom(null, async (get, set) => {
  const phone = get(phoneAtom).trim()
  const password = get(passwordAtom)

  if (!phone) throw new FormError('请输入手机号')
  if (!password) throw new FormError('请输入密码')

  set(isSubmittingAtom, true)
  try {
    let res: Response
    try {
      res = await api['sign-in'].$post({ json: { phone, password } })
    } catch (e) {
      // On sign-in specifically, 401 means wrong credentials, not "session expired".
      // apiFetch throws AuthRequiredError on 401, but here we want a FormError.
      if (e instanceof AuthRequiredError) {
        throw new FormError('手机号或密码错误')
      }
      throw e
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({ message: '登录失败' })) as { message?: string }
      throw new FormError(body.message || '登录失败')
    }
    const data = await res.json()
    set(currentUserAtom, data.user)
    await hydrateLocalForUser(data.user)
    return data
  } finally {
    set(isSubmittingAtom, false)
  }
})

export const registerAtom = atom(null, async (get, set) => {
  const phone = get(phoneAtom).trim()
  const password = get(passwordAtom)
  const confirmPassword = get(confirmPasswordAtom)
  const code = get(inviteCodeAtom).trim().toUpperCase()

  if (!/^1\d{10}$/.test(phone)) throw new FormError('手机号格式不正确')
  if (password.length < 6) throw new FormError('密码至少 6 位')
  if (password !== confirmPassword) throw new FormError('两次密码不一致')
  if (!code) throw new FormError('请输入邀请码')

  set(isSubmittingAtom, true)
  try {
    const res = await api.register.$post({ json: { phone, password, code } })
    if (!res.ok) {
      const body = await res.json().catch(() => ({ message: '注册失败' })) as { message?: string }
      throw new FormError(body.message || '注册失败')
    }
    const data = await res.json()
    set(currentUserAtom, data.user)
    await hydrateLocalForUser(data.user)
    return data
  } finally {
    set(isSubmittingAtom, false)
  }
})

export const currentUserAtom = atom<SessionUser>(null)

export const fetchSessionAtom = atom(null, async (_get, set) => {
  const res = await api['get-session'].$get()
  const data = await res.json()
  set(currentUserAtom, data.user)
  if (data.user) {
    await hydrateLocalForUser(data.user)
  } else {
    setLoggedIn(false)
  }
  return data.user
})

export const signOutAtom = atom(null, async (_get, set) => {
  await api['sign-out'].$post()
  set(currentUserAtom, null)
  setLoggedIn(false)
  // Local-first: keep IndexedDB data intact so the user can keep using the
  // app as a guest after signing out. Only drop the cached user identity.
  await clearUser()
})

export function loader(store: Store) {
  store.set(phoneAtom, '')
  store.set(passwordAtom, '')
  store.set(confirmPasswordAtom, '')
  store.set(inviteCodeAtom, '')
  store.set(modeAtom, 'login')
  store.set(isSubmittingAtom, false)
  return null
}
